import uuid from 'uuid';

import environmentNames from '../environmentNames';
import apiTools from './api/ApiTools';
import apiService from './api/ApiService';
import { responseCategories } from './api/ApiConstants';
import clientEventType from '../constants/ClientEventType';

window.addEventListener('message', (message) => {
    if (message.data.type === 'pdfjs-error') {
        console.error('pdfjs-error', message.data.error);
    }
});

function formatResponseError(error) {
    if (error.response?.status && error.response?.url) {
        return { status: error.response.status, url: error.response.url };
    }
    return error;
}

function isDiscardableError(error) {
    const errorIsDefined = error && error !== '';
    if (!errorIsDefined) {
        return true;
    }

    const errorIsResponseError = apiTools.isResponseError(error);
    if (errorIsResponseError) {
        if (error.errorType === responseCategories.OFFLINE
            || error.errorType === responseCategories.TIMEOUT
            || error.errorType === responseCategories.FORBIDDEN
            || error.errorType === responseCategories.NOT_LOGGED_IN) {
            return true;
        }
        if (error.response && error.response.url && error.response.url.includes('NotifyClientEvent')) {
            return true;
        }
    }

    return false;
}

export function logToExternalErrorTracking(error, hash, debuggingData) {
    let formattedError = error;

    if (isDiscardableError(error)) {
        return;
    }

    // For response errors we build a simplified object since TrackJS doesn't keep all values of the error objects
    if (apiTools.isResponseError(error)) {
        formattedError = `${JSON.stringify(formatResponseError(error))} - ${hash}`;
    }

    if (window.TrackJS) {
        if (debuggingData) {
            window.TrackJS.console.debug(debuggingData);
        }
        window.TrackJS.track(formattedError);
    } else {
        console.error(formattedError);
    }
}

function getErrorHash() {
    const hash = uuid.v4();
    return typeof hash === 'string' ? hash.substring(0, 8) : undefined;
}

function logResponseError(error, debuggingData) {
    const hash = getErrorHash();

    logToExternalErrorTracking(error, hash, debuggingData);

    // Network errors are still called server_error internally in the Live Monitor data model
    // URL is not included in Live event to keep the events generic and simple
    if (error.response) {
        apiService.notifyClientEvent(clientEventType.SERVER_ERROR, `${error.response.status} - ${hash}`);
    }
}

/**
 * Directly reporting errors to trackJs and Live Monitor as promises are swallowing the errors
 * http://jamesknelson.com/are-es6-promises-swallowing-your-errors/
 * @param error
 */
export function reportError(error, debuggingData) {
    const isUnitTestEnv = process.env.NODE_ENV === environmentNames.UNIT_TESTING;
    if (isUnitTestEnv) {
        return;
    }

    if (isDiscardableError(error)) {
        return;
    }

    if (apiTools.isResponseError(error)) {
        logResponseError(error, debuggingData);
    } else {
        // For non-response errors we still need to make sure they get logged to TrackJS
        logToExternalErrorTracking(error, null, debuggingData);
    }
}

export function configureErrorLogging() {
    window.addEventListener('unhandledrejection', (event) => {
        const reason = event.detail ? event.detail.reason : event.reason;
        reportError(reason);
    });
}

export function composeEnumerableErrorObject(error, extraProperties = []) {
    const parts = ['stack', ...extraProperties];
    const stringifiedParts = {
        message: error.toString(),
    };

    parts.forEach((part) => {
        stringifiedParts[part] = error[part] === undefined ? 'undefined' : error[part];
    });

    return stringifiedParts;
}

export function stringifyError(error, extraProperties) {
    const isErrorInstance = error instanceof Error;
    try {
        if (!isErrorInstance) {
            return JSON.stringify(error);
        }
        return JSON.stringify(composeEnumerableErrorObject(error, extraProperties));
    } catch (err) {
        return `Original error was not able to be fully stringified. ${err.toString()}`;
    }
}
